import { motion } from "framer-motion";
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { generateMockStory, type GeneratedStory } from "@/lib/mock-story";

const steps = [
  "Подбираем героя…",
  "Создаём волшебный мир…",
  "Добавляем помощника…",
  "Завершаем историю теплом…",
];

interface Props {
  name: string;
  gender: "he" | "she" | "";
  age: number;
  situation: string;
  goal: string;
  temperament: string;
  themes: string;
  tone: "calm" | "adventure" | "humor";
  length: "short" | "medium" | "long";
  heroFormat: "child" | "companion";
  onComplete: (storyData: GeneratedStory) => void;
}

const GenerationScreen = ({
  name,
  gender,
  age,
  situation,
  goal,
  temperament,
  themes,
  tone,
  length,
  heroFormat,
  onComplete,
}: Props) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [storyReady, setStoryReady] = useState<GeneratedStory | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleComplete = useCallback(
    (data: GeneratedStory) => onComplete(data),
    [onComplete]
  );

  // Start API call immediately
  useEffect(() => {
    let cancelled = false;

    const fetchStory = async () => {
      try {
        const safeGender = gender === "she" ? "she" : "he";
        const safeSituation = situation.trim() || "общая ситуация";
        const requestBody = { name, gender: safeGender, age, situation: safeSituation, goal, temperament, themes, tone, length, heroFormat };
        console.log("[Story] Request body:", JSON.stringify(requestBody));
        const { data, error: fnError } = await supabase.functions.invoke("generate-story", {
          body: requestBody,
        });

        if (cancelled) return;

        console.log("[Story] invoke result:", { data, fnError });

        if (fnError) {
          console.error("[Story] Edge function error:", fnError);
          throw new Error(fnError.message || "Ошибка генерации");
        }

        if (data?.error) {
          console.error("[Story] API returned error:", data.error);
          throw new Error(data.error);
        }

        if (!data?.story) {
          console.error("[Story] Missing story field in response:", data);
          throw new Error("Ответ не содержит историю");
        }

        console.log("[Story] Success, story length:", data.story?.length);
        setStoryReady(data as GeneratedStory);
      } catch (e) {
        if (cancelled) return;
        console.error("Story generation failed:", e);
        setError(e instanceof Error ? e.message : "Ошибка генерации");
        // Fallback to mock story
        setStoryReady(generateMockStory(name, gender));
      }
    };

    fetchStory();
    return () => { cancelled = true; };
  }, [name, gender, age, situation, goal, temperament, tone, length]);

  // Animate steps
  useEffect(() => {
    if (currentStep < steps.length) {
      const timer = setTimeout(() => setCurrentStep((prev) => prev + 1), 2000);
      return () => clearTimeout(timer);
    }
  }, [currentStep]);

  // When both animation done and story ready — proceed
  useEffect(() => {
    if (currentStep >= steps.length && storyReady) {
      const timer = setTimeout(() => handleComplete(storyReady), 500);
      return () => clearTimeout(timer);
    }
  }, [currentStep, storyReady, handleComplete]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="text-center space-y-8"
      >
        <motion.div
          animate={{ rotate: [0, 10, -10, 0] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          className="text-6xl"
        >
          🌙
        </motion.div>

        <h2 className="text-2xl font-serif text-foreground">
          Мы создаём историю…
        </h2>

        <div className="space-y-3 min-h-[160px]">
          {steps.map((step, i) => (
            <motion.p
              key={step}
              initial={{ opacity: 0, y: 10 }}
              animate={{
                opacity: i <= currentStep ? (i < currentStep ? 0.5 : 1) : 0,
                y: i <= currentStep ? 0 : 10,
              }}
              transition={{ duration: 0.4 }}
              className="text-lg font-sans text-muted-foreground"
            >
              {i < currentStep ? "✓" : "·"} {step}
            </motion.p>
          ))}

          {currentStep >= steps.length && !storyReady && !error && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-base font-sans text-muted-foreground"
            >
              Почти готово…
            </motion.p>
          )}
        </div>

        <div className="w-48 h-1.5 bg-muted rounded-full mx-auto overflow-hidden">
          <motion.div
            className="h-full bg-primary rounded-full"
            animate={{
              width: `${((Math.min(currentStep, steps.length) + 1) / (steps.length + 1)) * 100}%`,
            }}
            transition={{ duration: 0.5 }}
          />
        </div>
      </motion.div>
    </div>
  );
};

export default GenerationScreen;
